import { ArrowUpRight, ChevronLeft, ChevronRight, Facebook, Instagram, Play, Youtube } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.57L22.8 22h-6.9l-5.4-7.06L4.3 22H1.04l8.02-9.17L.4 2h7.07l4.88 6.45L18.24 2Zm-1.21 18h1.8L7.03 3.9H5.1L17.03 20Z" />
  </svg>
);
const SOCIAL_ICONS = { x: XIcon, instagram: Instagram, facebook: Facebook, youtube: Youtube };

const initials = (name) => name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export const PersonDialog = ({ person, onClose, onPrev, onNext }) => {
  const { t, lang } = useLang();
  if (!person) return null;
  const story = person.relatedStory;
  const socials = Object.entries(person.socialLinks || {}).filter(([, url]) => url);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="person-dialog"
        className="inset-x-0 bottom-0 top-auto block max-h-[92svh] w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-t-2xl border-line bg-coal p-0 text-cream shadow-2xl data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100 sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:max-h-[88vh] sm:max-w-[calc(100vw-3rem)] lg:max-w-4xl sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-md sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%] [&>button]:right-4 [&>button]:top-4 [&>button]:z-20 [&>button]:rounded-full [&>button]:border [&>button]:border-line [&>button]:bg-black/60 [&>button]:p-2 [&>button]:text-cream [&>button]:opacity-100 [&>button]:hover:border-ember [&>button]:hover:text-ember"
      >
        <div className="grid md:grid-cols-[300px_1fr]">
          {/* Portrait */}
          <div className="relative h-72 overflow-hidden bg-black md:h-auto md:min-h-[520px]">
            {person.portrait ? (
              <img
                src={person.portrait}
                alt={person.name[lang]}
                className="absolute inset-0 h-full w-full object-cover object-[center_8%]"
                data-testid="person-dialog-portrait"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center" data-testid="person-dialog-monogram">
                <span className="flex h-32 w-32 items-center justify-center rounded-full border border-ember/50 font-editorial text-5xl italic text-ember">
                  {initials(person.name.en)}
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-coal via-transparent to-transparent md:bg-gradient-to-r" />
          </div>

          {/* Profile */}
          <div className="relative p-6 md:p-10">
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-ember" data-testid="person-dialog-relationship">
              <span className="h-px w-8 bg-ember" aria-hidden="true" />
              {t.people.relationship[person.relationshipType]}
            </p>
            <DialogTitle className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-cream md:text-4xl" data-testid="person-dialog-name">
              {person.name[lang]}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm font-medium text-ash md:text-base" data-testid="person-dialog-profession">
              {person.profession[lang]}
            </DialogDescription>

            <p className="mt-6 text-sm leading-relaxed text-cream/85 md:text-base" data-testid="person-dialog-bio">
              {person.biography[lang]}
            </p>

            <div className="mt-6 border-l-2 border-ember/70 pl-4">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ash">{t.people.connectionLabel}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-cream/90" data-testid="person-dialog-connection">
                {person.connection[lang]}
              </p>
            </div>

            {story && (
              <a
                href={story.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="person-dialog-story-link"
                className="group mt-7 flex items-center gap-4 border border-line bg-card2 p-3 transition-colors duration-300 hover:border-ember/60"
              >
                {story.image ? (
                  <span className="relative block h-16 w-28 shrink-0 overflow-hidden">
                    <img
                      src={story.image}
                      alt=""
                      onError={(e) => {
                        if (story.imageFallback && e.currentTarget.src !== story.imageFallback) e.currentTarget.src = story.imageFallback;
                      }}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="h-5 w-5 fill-current text-cream" />
                    </span>
                  </span>
                ) : (
                  <span className="flex h-16 w-28 shrink-0 items-center justify-center bg-black">
                    <Youtube className="h-6 w-6 text-ember" />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-ember">{t.people.storyLabel}</span>
                  <span className="mt-1 line-clamp-2 block text-sm font-semibold leading-snug text-cream">{story.title}</span>
                  <span className="mt-1 block text-xs text-ash">{story.image ? t.people.watchStory : t.people.visitChannel}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-ash transition-colors group-hover:text-ember" />
              </a>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
              <div className="flex items-center gap-3">
                {socials.length > 0 && <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ash">{t.people.socialLabel}</span>}
                {socials.map(([key, url]) => {
                  const Icon = SOCIAL_ICONS[key];
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={key}
                      data-testid={`person-dialog-social-${key}`}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ash transition-colors duration-300 hover:border-ember hover:text-ember"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onPrev} aria-label={t.people.prev} data-testid="person-dialog-prev" className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-cream transition-colors duration-300 hover:border-ember hover:text-ember">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button type="button" onClick={onNext} aria-label={t.people.next} data-testid="person-dialog-next" className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-cream transition-colors duration-300 hover:border-ember hover:text-ember">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
